// Service layer between Phaser Scenes and the (prototype) sim. Scenes emit
// typed intents on gameBus; this controller applies them to the match, runs a
// world-step, localizes the resulting SimEvents into match.log, and signals the
// Scene with 'match:updated' / 'match:over'. This is exactly the seam where, in
// production, the calls become authoritative Django API requests.

import { gameBus } from '../bus/GameEventBus'
import { t, boonName, itemName, partLabel, weaponName } from '../i18n'
import { apiAction, apiNewMatch, isBackendEnabled, type BackendAction } from './JKGame_api'
import { type BackendEvent, localizeBackendEvents, summarizeBackendState } from './JKGame_backendLog'
import { toExploreView } from './JKGame_backendState'
import type { JKGameMsgKey } from '../i18n/zh-TW'
import { zoneName } from '../game/JKGame_assets'
import {
  applyChosenBoon,
  beginCombat,
  checkGameOver,
  computeRank,
  createMatch,
  pickupItem,
  pickupWeapon,
  playerAttack,
  playerEscape,
  playerFlee,
  playerMove,
  playerRest,
  playerSpecial,
  worldStep,
  type ActionOutcome,
  type FailKey,
  type SimEvent,
} from '../sim/JKGame_match'
import { JKGameRng } from '../sim/JKGame_rng'
import type { ExchangeResult, LogTone, MatchState, RestKind, WeaponKind } from '../sim/JKGame_types'
import { aliveEntities, getEntity, isForbidden, nextForbiddenZones, occupants } from '../sim/JKGame_world'
import type { BackendView } from './JKGame_backendState'
import { getHandle } from './JKGame_economy'
import { activeCharForBackend, activeLoadout } from './JKGame_loadout'
import { addSouls } from './JKGame_souls'

const MAX_LOG = 80

interface Line {
  text: string
  tone: LogTone
}

// ─── Source-agnostic read view ──────────────────────────────────────────────
// A normalized snapshot ExploreScene can read regardless of whether the match is
// driven by the local sim or the authoritative backend. Local mode maps from the
// in-memory MatchState; backend mode maps from a cached BackendView (set after
// each server action). Keeping the scene behind this seam lets the data source
// switch without touching the render path.
export interface PlayerView {
  zone: number
  hp: number
  mhp: number
  sp: number
  msp: number
  rage: number
  hasKey: boolean
  weaponName: string
  weaponKind: WeaponKind
  weaponAmmo: number // -1 = infinite (melee/fists); >=0 = rounds left before reverting to fists
  kills: number
  injuries: ReadonlySet<string>
  alive: boolean
}

export interface OccupantView {
  id: string
  name: string
  archetype: string
  hasKey: boolean
  isGhost: boolean // async-PvP ghost (a real rival's recorded run) → rendered distinctly
}

// Combat opponent — the subset CombatScene renders (hp bar / name / portrait).
export interface TargetView {
  id: string
  name: string
  archetype: string
  hp: number
  mhp: number
  alive: boolean
  hasKey: boolean
}

const FAIL_KEYS: Record<FailKey, JKGameMsgKey> = {
  forbidden: 'fail.forbidden',
  sameZone: 'fail.sameZone',
  noSp: 'fail.noSp',
  deadAct: 'fail.deadAct',
  noKey: 'fail.noKey',
  notRunning: 'fail.notRunning',
  noRage: 'fail.noRage',
  noBoon: 'fail.noBoon',
}

// Zone-name localizer: delegate to the GUARDED assets.zoneName (hasMsg + 'zone.unknown'
// fallback) instead of an unguarded `zone.${id}.name` cast — single source of truth, and an
// unexpected zone can't leak a raw key. (backendLog already imports zoneName from assets.)
const zname = zoneName

class MatchController {
  private match: MatchState
  private rng: JKGameRng
  private bound = false
  private backendId: string | null = null
  // True once the authoritative backend session is live; the action path then
  // routes through the server and the local idle world-step is suppressed.
  private backendMode = false
  // Latest authoritative snapshot (set after each backend action). Null while
  // running on the local sim — the unified getters then read the local match.
  private backendView: BackendView | null = null
  // One-shot guard so local-mode souls settlement pays out exactly once per match
  // (Result.create can re-run on scene restart). Reset in newMatch.
  private localSettled = false
  // Backend-session replay record: the seed/char/bots the match was created with, plus the EXACT
  // action sequence sent to the server (in order). Result settles via /settle_replay with this, so
  // the server re-derives the authoritative result (A-方案) instead of trusting a reported rank.
  private backendSeed = 0
  private backendChar: string | null = null
  private backendBots = 41
  private backendActions: BackendAction[] = []
  private backendRanked = false // the mode the live backend session was created with
  // Selected match mode (ranked = fair: no loadout/boon, separate board). Set from the Menu before
  // newMatch/startBackendMatch; persists across "play again".
  private ranked = false

  constructor() {
    this.match = createMatch(1, undefined, activeLoadout())
    this.rng = new JKGameRng(3)
    this.bindIntents()
  }

  getMatch(): MatchState {
    return this.match
  }

  getRank(): { rank: number; total: number } {
    return computeRank(this.match)
  }

  getBackendId(): string | null {
    return this.backendId
  }

  // ─── Unified read view (source-agnostic) ──────────────────────────────────
  // Each getter reads the cached BackendView when present, else the local match.
  // backendView stays null until the backend action path is wired, so today
  // every getter takes the local branch → behavior identical to getMatch().

  /** True when the unified getters are serving authoritative server state. */
  isBackendView(): boolean {
    return this.backendView !== null
  }

  /** Cache the latest authoritative snapshot (called by the backend action path,
   *  not yet wired). Triggers a scene refresh. */
  cacheBackendView(view: BackendView): void {
    this.backendView = view
    this.signal()
  }

  getWeather(): number {
    return this.backendView ? this.backendView.weather : this.match.weather
  }

  getAliveCount(): number {
    return this.backendView ? this.backendView.alive : aliveEntities(this.match).length
  }

  getTotal(): number {
    return this.backendView ? this.backendView.total : this.match.entities.length
  }

  getStepsToShrink(): number {
    return this.backendView ? this.backendView.stepsToShrink : this.match.stepsToShrink
  }

  getStep(): number {
    return this.backendView ? this.backendView.step : this.match.step
  }

  getRankView(): { rank: number; total: number } {
    if (this.backendView) return { rank: this.backendView.rank, total: this.backendView.total }
    return computeRank(this.match)
  }

  getWinReason(): 'survivor' | 'escape' | 'dead' | null {
    return this.backendView ? this.backendView.winReason : this.match.winReason ?? null
  }

  isZoneForbidden(zone: number): boolean {
    return this.backendView ? this.backendView.forbidden.has(zone) : isForbidden(this.match, zone)
  }

  getNextForbidden(): number[] {
    return this.backendView ? [...this.backendView.nextForbidden] : nextForbiddenZones(this.match)
  }

  /** The id of the combatant the player is currently engaged with, or null when
   *  not in combat (server-authoritative in backend mode). */
  getPendingTargetId(): string | null {
    return this.backendView ? this.backendView.pendingTargetId : this.match.pendingTargetId
  }

  getPlayerView(): PlayerView {
    const v = this.backendView
    if (v) {
      const p = v.player
      return {
        zone: p.zone,
        hp: p.hp,
        mhp: p.mhp,
        sp: p.sp,
        msp: p.msp,
        rage: p.rage,
        hasKey: p.has_key,
        weaponName: p.weapon.name,
        weaponKind: p.weapon.kind as WeaponKind,
        weaponAmmo: p.weapon.ammo,
        kills: p.kills,
        injuries: v.injurySet,
        alive: p.alive,
      }
    }
    const p = getEntity(this.match, this.match.playerId)
    return {
      zone: p?.zone ?? 0,
      hp: p?.hp ?? 0,
      mhp: p?.mhp ?? 1,
      sp: p?.sp ?? 0,
      msp: p?.msp ?? 1,
      rage: p?.rage ?? 0,
      hasKey: p?.hasKey ?? false,
      weaponName: p?.weapon.name ?? '',
      weaponKind: p?.weapon.kind ?? 'N',
      weaponAmmo: p?.weapon.ammo ?? -1,
      kills: p?.kills ?? 0,
      injuries: p?.injuries ?? new Set<string>(),
      alive: p?.alive ?? false,
    }
  }

  /** Combat opponent snapshot by id (source-agnostic). Null if unknown/gone. */
  getTargetView(id: string): TargetView | null {
    if (this.backendView) {
      const e = this.backendView.entitiesById.get(id)
      return e ? { id: e.id, name: e.name, archetype: e.archetype, hp: e.hp, mhp: e.mhp, alive: e.alive, hasKey: e.has_key } : null
    }
    const e = getEntity(this.match, id)
    return e ? { id: e.id, name: e.name, archetype: e.archetype, hp: e.hp, mhp: e.mhp, alive: e.alive, hasKey: e.hasKey } : null
  }

  getOccupantsAt(zone: number): OccupantView[] {
    if (this.backendView) {
      const list = this.backendView.occupantsByZone.get(zone) ?? []
      return list.map((e) => ({ id: e.id, name: e.name, archetype: e.archetype, hasKey: e.has_key, isGhost: !!e.is_ghost }))
    }
    // Local sim has no ghosts (ghosts are an online-only feature) → isGhost always false.
    return occupants(this.match, zone, 'P').map((e) => ({ id: e.id, name: e.name, archetype: e.archetype, hasKey: e.hasKey, isGhost: false }))
  }

  /** Opt-in: create the same match on the authoritative backend and log its
   *  server-side state. No-op unless the backend flag is on; the local sim still
   *  drives the playable match. The next step routes actions through this id. */
  async startBackendMatch(seed: number): Promise<void> {
    this.backendId = null
    this.backendMode = false
    if (!isBackendEnabled()) return
    try {
      // ranked ignores the loadout (fair) → send no char; casual deploys the chosen character.
      const char = this.ranked ? null : activeCharForBackend()
      // pass the handle so a ranked match loads OTHERS' ghosts on this seed (excluding our own).
      const r = await apiNewMatch(seed, undefined, char, this.ranked, getHandle()) // create the match server-side
      this.backendId = r.id
      // Start the replay record for this session (Result settles via /settle_replay).
      this.backendSeed = seed
      this.backendChar = char
      this.backendBots = 41 // apiNewMatch default; keep in sync if that changes
      this.backendRanked = this.ranked
      this.backendActions = []
      this.backendMode = true
      this.push([summarizeBackendState(r.state)])
      // Serve the unified getters from authoritative state immediately.
      this.cacheBackendView(toExploreView(r.state))
    } catch (e) {
      console.warn('[JKGame] backend session failed:', e)
    }
  }

  /** True once the backend session is driving the match (suppresses local idle). */
  isBackendMode(): boolean {
    return this.backendMode
  }

  /** Start a fresh match with the given seed and seed the runtime RNG from it. */
  newMatch(seed: number): void {
    // Apply the player's active 出戰角色 loadout (start weapon + passive). RNG-free → the seed
    // still fully determines the match; only the player's opener differs (docs/playability-monetization.md).
    this.match = createMatch(seed, undefined, activeLoadout(), this.ranked)
    this.rng = new JKGameRng(seed * 2 + 1)
    this.backendView = null // local match drives the getters until backend wiring
    this.backendMode = false
    this.localSettled = false // fresh match → souls payable again
    this.backendActions = [] // drop any prior session's replay record
    this.backendId = null // fully detach from any prior backend session (startBackendMatch re-sets
    // it when wiring a new one). Inert today since backendMode gates the backend path, but this
    // keeps newMatch a COMPLETE reset so a future caller that skips startBackendMatch can't leak
    // a stale session id into a later backendAction.
    const p = getEntity(this.match, this.match.playerId)
    const zoneNm = p ? zname(p.zone) : ''
    this.push([{ text: t('log.deploy', { zone: zoneNm }), tone: 'system' }])
    const upcoming = nextForbiddenZones(this.match)
    if (upcoming.length > 0) {
      this.push([{ text: t('log.broadcast', { zones: upcoming.map(zname).join('、') }), tone: 'system' }])
    }
  }

  /** Advance the world while the player is idle so bots keep fighting.
   *  No-op in backend mode — the server advances the world on each action. */
  idleTick(): void {
    if (this.backendMode) return
    if (this.match.status !== 'running' || this.match.pendingTargetId) return
    const evs = worldStep(this.match, this.rng)
    this.push(this.localizeAll(evs))
    checkGameOver(this.match)
    this.signal()
  }

  // ─── intent wiring ─────────────────────────────────────────────────────────

  private bindIntents(): void {
    if (this.bound) return
    this.bound = true
    gameBus.on('intent:move', ({ zone }) => {
      if (this.backendMode) void this.backendAction({ type: 'move', zone })
      else this.runAction(() => playerMove(this.match, zone, this.rng))
    })
    gameBus.on('intent:rest', ({ kind }: { kind: RestKind }) => {
      if (this.backendMode) void this.backendAction({ type: 'rest', kind })
      else this.runAction(() => playerRest(this.match, kind, this.rng))
    })
    gameBus.on('intent:attack', ({ targetId }) => {
      if (this.backendMode) void this.backendAction({ type: 'attack', target_id: targetId })
      else this.runAction(() => playerAttack(this.match, targetId, this.rng))
    })
    gameBus.on('intent:special', ({ targetId }) => {
      if (this.backendMode) void this.backendAction({ type: 'special', target_id: targetId })
      else this.runAction(() => playerSpecial(this.match, targetId, this.rng))
    })
    gameBus.on('intent:flee', () => {
      if (this.backendMode) void this.backendAction({ type: 'flee' })
      else this.runAction(() => playerFlee(this.match, this.rng))
    })
    gameBus.on('intent:escape', () => {
      if (this.backendMode) void this.backendAction({ type: 'escape' })
      else this.runAction(() => playerEscape(this.match), false)
    })
    gameBus.on('intent:beginCombat', ({ targetId }) => {
      if (this.backendMode) {
        // begin does not world-step (TICK_ACTIONS); it only sets pending_target_id.
        void this.backendAction({ type: 'begin', target_id: targetId })
      } else {
        beginCombat(this.match, targetId)
        this.signal()
      }
    })
    gameBus.on('intent:pickupWeapon', ({ weapon }) => {
      if (this.backendMode) {
        // Backend owns authoritative loot; the explore-room loot is client-only
        // decoration. Say so plainly rather than implying a real pickup.
        this.push([{ text: t('log.pickup.serverManaged', { item: weaponName(weapon.name) }), tone: 'system' }])
        this.signal()
        return
      }
      pickupWeapon(this.match, weapon)
      this.push([{ text: t('log.search.weapon', { item: weaponName(weapon.name) }), tone: 'good' }])
      this.signal()
    })
    gameBus.on('intent:pickupItem', ({ item }) => {
      if (this.backendMode) {
        this.push([{ text: t('log.pickup.serverManaged', { item: itemName(item) }), tone: 'system' }])
        this.signal()
        return
      }
      pickupItem(this.match, item)
      this.push([{ text: t('log.search.item', { item: itemName(item) } ), tone: 'good' }])
      this.signal()
    })
    gameBus.on('intent:chooseBoon', ({ boonId }) => {
      // Picking a run buff is a FREE meta-action — it doesn't advance the world. Backend mode
      // routes it to the authoritative server (choose_boon action); local mode applies the sim.
      if (this.backendMode) void this.backendAction({ type: 'choose_boon', boon_id: boonId })
      else this.runAction(() => applyChosenBoon(this.match, boonId), false)
    })
  }

  /** The three boon ids currently offered to the player (after a kill), or null if none pending.
   *  Scenes read this to show the three-choice overlay. Source-agnostic (backend view or local). */
  getPendingBoon(): string[] | null {
    if (this.backendView) return this.backendView.pendingBoon
    return this.match.pendingBoon
  }

  /** Run buffs the player has taken this match (for a HUD/summary readout). Source-agnostic. */
  getBoons(): string[] {
    return this.backendView ? this.backendView.boons : this.match.boons
  }

  /** Select the match mode for the NEXT match (ranked = fair: no loadout/boon, ranked board).
   *  Persists across "play again" until changed. Set from the Menu before starting a match. */
  setRanked(ranked: boolean): void {
    this.ranked = ranked
  }

  /** Whether the selected mode is ranked (for Menu/Result UI). */
  isRanked(): boolean {
    return this.ranked
  }

  /** The authoritative-settlement payload for a backend match: the seed/char/bots/ranked + the exact
   *  action sequence, for /settle_replay. Null in local mode (no server session to re-derive). */
  getReplayPayload(): { seed: number; bots: number; char: string | null; actions: BackendAction[]; ranked: boolean } | null {
    if (!this.backendMode || !this.backendId) return null
    return { seed: this.backendSeed, bots: this.backendBots, char: this.backendChar, actions: this.backendActions, ranked: this.backendRanked }
  }

  /** Local-mode (flag-off) settlement: award permanent souls ONCE per finished match, from the
   *  player's placement + kills + survival. Backend mode settles server-side (anti-cheat clamp /
   *  future seed-replay), so this no-ops there. Returns the breakdown for the Result screen, or
   *  null if nothing to award (backend mode, not over yet, or already settled). */
  settleLocalSouls(): { souls: number; balance: number } | null {
    if (this.backendMode || this.backendView) return null
    if (this.localSettled || this.match.status !== 'over') return null
    this.localSettled = true
    const { rank, total } = computeRank(this.match)
    const p = getEntity(this.match, this.match.playerId)
    const kills = p?.kills ?? 0
    const steps = this.match.step
    const placement = rank === 1 ? 40 : rank <= Math.ceil(total * 0.1) ? 20 : rank <= Math.ceil(total * 0.5) ? 8 : 2
    const souls = 10 + kills * 5 + placement + Math.floor(steps / 25)
    return { souls, balance: addSouls(souls) }
  }

  // ─── backend action path (authoritative; opt-in via flag) ──────────────────

  /** Route a player action to the authoritative server, then refresh the cached
   *  view. The server advances the world; the local sim is untouched in this
   *  mode. Rejections (forbidden / no SP / no key …) are localized into the log. */
  private async backendAction(action: BackendAction): Promise<void> {
    if (!this.backendId) return
    try {
      const { result, state } = await apiAction(this.backendId, action)
      // Record the exact action the server processed (ok or not — a rejected action is a
      // deterministic no-op on replay too), preserving order for /settle_replay re-derivation.
      this.backendActions.push(action)
      const r = result as { ok: boolean; fail: string | null; events?: unknown[] }
      if (!r.ok) {
        const msgKey: JKGameMsgKey =
          r.fail && Object.prototype.hasOwnProperty.call(FAIL_KEYS, r.fail)
            ? FAIL_KEYS[r.fail as FailKey]
            : 'fail.generic'
        this.push([{ text: t(msgKey), tone: 'bad' }])
        this.cacheBackendView(toExploreView(state)) // refresh (state unchanged)
        return
      }
      // Localize backend events through the PURPOSE-BUILT backend localizer (backendLog), which
      // reads the authoritative sim's snake_case JSON directly (att_hit / attacker / is_player)
      // and handles the backend 'defend' kind natively — instead of the local-sim localizer
      // (localizeAll), whose camelCase shape would need ad-hoc snake→camel/kind reshaping.
      // (localizeBackendEvent mirrors localizePlayer*() line-for-line; same i18n keys, same lines.)
      if (r.events) this.push(localizeBackendEvents(r.events as unknown as BackendEvent[]))
      this.cacheBackendView(toExploreView(state)) // emits match:updated
      if (state.status === 'over') {
        gameBus.emit('match:over', { winnerId: null, reason: state.win_reason ?? 'dead' })
      }
    } catch (e) {
      console.warn('[JKGame] backend action failed:', e)
      // Never silent: a network/server error must still tell the player why their tap did
      // nothing. A THROWN error is a transient failure (didn't reach the server / server
      // errored), so 'log.actionFailed' ("failed, please retry") reads accurately — unlike
      // fail.generic ("can't be performed"), which wrongly implies the action was invalid.
      // signal renders it (and any pending combat 'fleeing' guard recovers on the next update);
      // the authoritative state is unchanged, so this only adds a log line — no phantom progress.
      this.push([{ text: t('log.actionFailed'), tone: 'bad' }])
      this.signal()
    }
  }

  /** Apply a player action; by default advance the world one step afterwards. */
  private runAction(apply: () => ActionOutcome, tick = true): void {
    if (this.match.status !== 'running') return
    const outcome = apply()
    if (!outcome.ok) {
      if (outcome.failKey) this.push([{ text: t(FAIL_KEYS[outcome.failKey]), tone: 'bad' }])
      this.signal()
      return
    }
    this.push(this.localizeAll(outcome.events))

    // The player's own action may have ended the match (died / escaped).
    checkGameOver(this.match)
    if (tick && this.match.status === 'running') {
      const wevs = worldStep(this.match, this.rng)
      this.push(this.localizeAll(wevs))
      checkGameOver(this.match)
    }
    this.signal()
  }

  // ─── logging ────────────────────────────────────────────────────────────────

  private push(lines: Line[]): void {
    for (const l of lines) this.match.log.push({ step: this.match.step, text: l.text, tone: l.tone })
    if (this.match.log.length > MAX_LOG) this.match.log.splice(0, this.match.log.length - MAX_LOG)
  }

  private signal(): void {
    gameBus.emit('match:updated', {})
    if (this.match.status === 'over') {
      gameBus.emit('match:over', { winnerId: this.match.winnerId, reason: this.match.winReason ?? 'dead' })
    }
  }

  private localizeAll(events: SimEvent[]): Line[] {
    const out: Line[] = []
    for (const ev of events) out.push(...this.localize(ev))
    return out
  }

  private playerZoneName(): string {
    const p = getEntity(this.match, this.match.playerId)
    return p ? zname(p.zone) : ''
  }

  private localize(ev: SimEvent): Line[] {
    switch (ev.k) {
      case 'move':
        return [{ text: t('log.move', { zone: zname(ev.zone) }), tone: 'normal' }]
      case 'searchNothing':
        return [{ text: t('log.search.nothing'), tone: 'normal' }]
      case 'searchItem':
        return [{ text: t('log.search.item', { item: itemName(ev.item) }), tone: 'good' }]
      case 'searchWeapon':
        return [{ text: t('log.search.weapon', { item: weaponName(ev.item) }), tone: 'good' }]
      case 'restSp':
        // Resting at full SP wastes a turn (button is dimmed, not disabled): report the
        // honest "already full" line instead of a confusing "recovered 0".
        return [{ text: ev.n > 0 ? t('log.rest.sp', { n: ev.n }) : t('log.rest.spFull'), tone: 'normal' }]
      case 'restHp':
        return [{ text: ev.n > 0 ? t('log.rest.hp', { n: ev.n }) : t('log.rest.hpFull'), tone: 'normal' }]
      case 'hazard':
        return [{ text: t('log.hazard', { n: ev.dmg }), tone: 'bad' }]
      case 'encounter':
        return [{ text: t('log.encounter', { zone: this.playerZoneName(), name: ev.name }), tone: 'normal' }]
      case 'ambush':
        return [{ text: t('log.ambush', { name: ev.name }), tone: 'bad' }]
      case 'youKilled':
        return [{ text: t('log.youKilled', { name: ev.name }), tone: 'good' }]
      case 'youDied':
        return [{ text: t('log.youDied', { name: ev.name }), tone: 'bad' }]
      case 'youDiedEnv':
        return [{ text: t('log.youDiedEnv'), tone: 'bad' }]
      case 'keyTaken':
        return [{ text: t('log.keyDrop', { name: ev.name }), tone: 'good' }]
      case 'targetGone':
        return [{ text: t('log.targetGone'), tone: 'normal' }]
      case 'fled':
        return [{ text: t('log.fled'), tone: 'normal' }]
      case 'special':
        return [{ text: t('log.special'), tone: 'good' }]
      case 'botKill':
        return [{ text: t('log.botKill', { killer: ev.killer, victim: ev.victim }), tone: 'system' }]
      case 'collar':
        return [{ text: t('log.collar', { name: ev.name }), tone: ev.isPlayer ? 'bad' : 'system' }]
      case 'keyLost':
        return [{ text: t('log.keyLost', { name: ev.name }), tone: 'system' }]
      case 'broadcast':
        return [{ text: t('log.broadcast', { zones: ev.zones.map(zname).join('、') }), tone: 'system' }]
      case 'playerExchange':
        return this.localizePlayerAttack(ev.r)
      case 'defendExchange':
        return this.localizePlayerDefend(ev.r)
      case 'boonOffer':
        return [{ text: t('log.boonOffer'), tone: 'good' }]
      case 'boonTaken':
        return [{ text: t('log.boonTaken', { name: boonName(ev.id) }), tone: 'good' }]
      default: {
        // Compile-time exhaustiveness: if a new SimEvent kind is added without a case above,
        // `ev` is no longer `never` here and tsc fails the build — turning a would-be SILENT
        // dropped log line (this is the LOCAL-sim, flag-off path) into a hard error. It's the
        // compile-time analog of backend test #42 (which guards the backend-event vocabulary);
        // motivated by the parallel sim work actively adding kinds (e.g. keyLost). The runtime
        // [] stays as a belt-and-braces fallback so a kind that somehow slips past the types
        // degrades to "no log line" rather than letting localizeAll's spread throw a TypeError.
        const _exhaustive: never = ev
        void _exhaustive
        return []
      }
    }
  }

  // NOTE: localizePlayerAttack/localizePlayerDefend MUST emit the same lines as
  // backendLog.localizeAtt/localizeDef (the backend-sim twins) — same i18n keys, tones, order —
  // differing ONLY in field case (camel here, snake there). Keep the pair in sync.
  private localizePlayerAttack(r: ExchangeResult): Line[] {
    const out: Line[] = []
    if (r.attHit) {
      const base = t('log.attackHit', { name: r.defenderName, dmg: r.attDmg })
      out.push({ text: r.attCrit ? `${t('log.crit')} ${base}` : base, tone: 'good' })
      if (r.attInjury) out.push({ text: t('log.injury', { who: r.defenderName, part: partLabel(r.attInjury) }), tone: 'good' })
    } else {
      out.push({ text: t('log.attackMiss'), tone: 'normal' })
    }
    if (r.countered && r.defDmg > 0) {
      out.push({ text: t('log.counterHit', { name: r.defenderName, dmg: r.defDmg }), tone: 'bad' })
      if (r.defInjury) out.push({ text: t('log.injurySelf', { part: partLabel(r.defInjury) }), tone: 'bad' })
    }
    return out
  }

  private localizePlayerDefend(r: ExchangeResult): Line[] {
    const out: Line[] = []
    if (r.attHit) {
      out.push({ text: t('log.enemyHit', { name: r.attackerName, dmg: r.attDmg }), tone: 'bad' })
      if (r.attInjury) out.push({ text: t('log.injurySelf', { part: partLabel(r.attInjury) }), tone: 'bad' })
    } else {
      out.push({ text: t('log.enemyMiss', { name: r.attackerName }), tone: 'normal' })
    }
    if (r.countered && r.defDmg > 0) {
      out.push({ text: t('log.attackHit', { name: r.attackerName, dmg: r.defDmg }), tone: 'good' })
      if (r.defInjury) out.push({ text: t('log.injury', { who: r.attackerName, part: partLabel(r.defInjury) }), tone: 'good' })
    }
    return out
  }
}

// Singleton service for the slice.
export const matchController = new MatchController()
